import marketRoutes from "@/data/market-routing-catalog.json";
import { SITE_LOCALES, type SiteLocale } from "@/lib/i18n/config";
import type { NavCustomPath, ResolvedMarketingRoute } from "@/lib/i18n/paths";

/**
 * Locales whose marketing pages are a curated subset with their own URL
 * segments are defined by the Norwegian, Danish, German, Dutch, Spanish, French, Portuguese, Italian and Finnish JSON catalogs.
 * A page that is not listed in its catalog has no version in that language:
 * omit it from navigation and hreflang, and send visitors to the fallback
 * locale's page instead.
 */

type MarketSubpage = {
  slug: string;
  keyword: string;
  /** Semantic subject when it differs from the other languages (e.g. e-conomic vs Fortnox). */
  identity?: string;
  /** Existing sub-page implementation to render for a market-only subject. */
  implementation?: string;
};

type MarketService = {
  slug: string;
  keyword: string;
  title: string;
  subpages: Record<string, MarketSubpage>;
};

type MarketCatalog = {
  fallbackLocale: SiteLocale;
  pages: Set<string>;
  customPaths: Partial<Record<NavCustomPath, string>>;
  services: Record<string, MarketService>;
};

function catalog(data: {
  fallbackLocale?: string;
  pages: string[];
  customPaths?: Record<string, string>;
  services: unknown;
}): MarketCatalog {
  return {
    fallbackLocale: (data.fallbackLocale ?? "sv") as SiteLocale,
    pages: new Set(data.pages),
    customPaths: (data.customPaths ?? {}) as Partial<Record<NavCustomPath, string>>,
    services: data.services as Record<string, MarketService>,
  };
}

const CATALOGS = Object.fromEntries(
  Object.entries(marketRoutes).map(([locale, data]) => [locale, catalog(data)]),
) as Partial<Record<SiteLocale, MarketCatalog>>;

export function isMarketLocale(locale: string): locale is SiteLocale {
  return locale in CATALOGS;
}

function catalogFor(locale: string): MarketCatalog | undefined {
  return CATALOGS[locale as SiteLocale];
}

/** Whether the page exists in this locale; always true for full-site locales. */
export function isMarketRouteAvailable(locale: string, route: ResolvedMarketingRoute): boolean {
  const market = catalogFor(locale);
  if (!market) return true;
  switch (route.kind) {
    case "service":
      return route.serviceTag in market.services;
    case "service-subpage":
      return route.subKey in (market.services[route.serviceTag]?.subpages ?? {});
    default:
      return market.pages.has(route.kind);
  }
}

/** Locale whose page a market visitor sees when the page has no market version. */
export function marketFallbackLocale(locale: string): SiteLocale | undefined {
  return catalogFor(locale)?.fallbackLocale;
}

/** Market URL segment of a top-level page, when the catalog defines one. */
export function getMarketCustomPathSegment(locale: string, customPath: NavCustomPath): string | undefined {
  return catalogFor(locale)?.customPaths[customPath];
}

/** Top-level page for a market URL segment. */
export function getCustomPathByMarketSegment(locale: string, segment: string): NavCustomPath | undefined {
  const paths = catalogFor(locale)?.customPaths ?? {};
  return (Object.entries(paths).find(([, value]) => value === segment)?.[0] as NavCustomPath | undefined);
}

export function getMarketServiceSlug(locale: string, serviceTag: string): string | undefined {
  return catalogFor(locale)?.services[serviceTag]?.slug;
}

export function getServiceTagByMarketSlug(locale: string, slug: string): string | undefined {
  const services = catalogFor(locale)?.services ?? {};
  return Object.entries(services).find(([, service]) => service.slug === slug)?.[0];
}

export function getMarketSubpageSlug(locale: string, serviceTag: string, subKey: string): string | undefined {
  return catalogFor(locale)?.services[serviceTag]?.subpages[subKey]?.slug;
}

export function getSubpageKeyByMarketSlug(locale: string, serviceTag: string, slug: string): string | undefined {
  const subpages = catalogFor(locale)?.services[serviceTag]?.subpages ?? {};
  return Object.entries(subpages).find(([, page]) => page.slug === slug)?.[0];
}

/** Market-only semantic identity of a sub-page (see `alternateIdentity` in nav-services-mega.json). */
export function getMarketSubpageIdentity(locale: string, serviceTag: string, subKey: string): string | undefined {
  return catalogFor(locale)?.services[serviceTag]?.subpages[subKey]?.identity;
}

/** Sub-page implementation key: market-only subjects reuse an existing page design. */
export function getMarketSubpageImplementation(locale: string, serviceTag: string, subKey: string): string {
  return catalogFor(locale)?.services[serviceTag]?.subpages[subKey]?.implementation ?? subKey;
}

/** Market-only sub-pages that are not in the shared service navigation. */
export function listMarketOnlySubpages(
  locale: string,
  isShared: (serviceTag: string, subKey: string) => boolean,
): { serviceTag: string; subKey: string }[] {
  const services = catalogFor(locale)?.services ?? {};
  return Object.entries(services).flatMap(([serviceTag, service]) =>
    Object.keys(service.subpages)
      .filter((subKey) => !isShared(serviceTag, subKey))
      .map((subKey) => ({ serviceTag, subKey })),
  );
}

/** Site locales in this environment that have this page (blog categories, shared pages). */
export function siteLocalesWithPage(route: ResolvedMarketingRoute): SiteLocale[] {
  return SITE_LOCALES.filter((locale) => isMarketRouteAvailable(locale, route));
}
